import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { humanise, inr, num } from '../lib/format';
import { Card, CardHead, Empty, Notice } from '../components/ui';

interface Overview {
  cards: {
    users: number;
    activeUsers30d: number;
    newUsers7d: number;
    blocked: number;
    locked: number;
    admins: number;
    sellers: number;
    providers: number;
    partners: number;
    liveListings: number;
    activeSessions: number;
    failedLogins24h: number;
    revenuePaise: number;
  };
  registrations: Array<{ date: string; count: number }>;
  logins: Array<{ date: string; success: number; failed: number }>;
  byRole: Array<{ role: string; count: number }>;
  topLocations: Array<{ label: string; count: number }>;
}

function Stat({ label, value, to, tone }: { label: string; value: string; to?: string; tone?: 'danger' | 'amber' }) {
  const body = (
    <div className="stat">
      <div>
        <div className="stat__label">{label}</div>
        <div className="stat__value" style={tone ? { color: `var(--${tone})` } : undefined}>
          {value}
        </div>
      </div>
    </div>
  );
  return to ? (
    <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
      {body}
    </Link>
  ) : (
    body
  );
}

/** A row of bars, one per day. No chart library: fourteen numbers do not need one. */
function Bars({
  days,
  series,
}: {
  days: string[];
  series: Array<{ label: string; color: string; values: number[] }>;
}) {
  const max = Math.max(1, ...series.flatMap((s) => s.values));
  return (
    <div style={{ padding: 'var(--s4) var(--s5) var(--s5)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 140 }}>
        {days.map((day, i) => (
          <div key={day} style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 2, height: '100%' }} title={day}>
            {series.map((s) => (
              <div
                key={s.label}
                title={`${day} · ${s.label}: ${s.values[i]}`}
                style={{
                  flex: 1,
                  height: `${Math.max(s.values[i] > 0 ? 4 : 0, (s.values[i] / max) * 100)}%`,
                  background: s.color,
                  borderRadius: '3px 3px 0 0',
                }}
              />
            ))}
          </div>
        ))}
      </div>
      <div className="row" style={{ justifyContent: 'space-between', marginTop: 6, fontSize: 11.5, color: 'var(--ink-muted)' }}>
        <span>{days[0]}</span>
        <span className="row" style={{ gap: 12 }}>
          {series.map((s) => (
            <span key={s.label} className="row" style={{ gap: 5 }}>
              <span style={{ width: 9, height: 9, borderRadius: 2, background: s.color, display: 'inline-block' }} />
              {s.label}
            </span>
          ))}
        </span>
        <span>{days[days.length - 1]}</span>
      </div>
    </div>
  );
}

export function MasterDashboard() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['master-overview'],
    queryFn: () => api.get<Overview>('/master/overview'),
    refetchInterval: 60_000,
  });

  if (isLoading) return <div className="skeleton" style={{ height: 360 }} />;
  if (isError) return <Empty icon="⚠️" title="Could not load the overview" body={(error as Error).message} />;
  if (!data) return null;

  const c = data.cards;

  return (
    <div className="stack" style={{ gap: 'var(--s5)' }}>
      {c.failedLogins24h >= 10 && (
        <Notice tone="danger" title={`${num(c.failedLogins24h)} failed sign-ins in the last 24 hours`}>
          That is more than usual. <Link to="/security">Open Security</Link> to see who was refused.
        </Notice>
      )}

      <div className="grid grid--stats">
        <Stat label="Total users" value={num(c.users)} to="/users" />
        <Stat label="Active in 30 days" value={num(c.activeUsers30d)} />
        <Stat label="New this week" value={num(c.newUsers7d)} />
        <Stat label="Signed in now" value={num(c.activeSessions)} to="/login-history?active=true" />
        <Stat label="Sellers" value={num(c.sellers)} to="/users?role=SELLER" />
        <Stat label="Service providers" value={num(c.providers)} to="/users?role=SERVICE_PROVIDER" />
        <Stat label="Partners" value={num(c.partners)} to="/users?role=AGENT" />
        <Stat label="Admins" value={num(c.admins)} to="/admins" />
        <Stat label="Live listings" value={num(c.liveListings)} />
        <Stat label="Revenue collected" value={inr(c.revenuePaise)} />
        <Stat
          label="Blocked accounts"
          value={num(c.blocked)}
          to="/users?blocked=true"
          tone={c.blocked > 0 ? 'amber' : undefined}
        />
        <Stat
          label="Failed sign-ins, 24 h"
          value={num(c.failedLogins24h)}
          to="/login-history?status=FAILED"
          tone={c.failedLogins24h > 0 ? 'danger' : undefined}
        />
      </div>

      <div className="grid grid--2">
        <Card flush>
          <CardHead title="New registrations" sub="Last 14 days" />
          <Bars
            days={data.registrations.map((d) => d.date.slice(5))}
            series={[{ label: 'Registrations', color: 'var(--blue)', values: data.registrations.map((d) => d.count) }]}
          />
        </Card>
        <Card flush>
          <CardHead title="Sign-ins" sub="Last 14 days" />
          <Bars
            days={data.logins.map((d) => d.date.slice(5))}
            series={[
              { label: 'Successful', color: 'var(--success)', values: data.logins.map((d) => d.success) },
              { label: 'Failed', color: 'var(--danger)', values: data.logins.map((d) => d.failed) },
            ]}
          />
        </Card>
      </div>

      <div className="grid grid--2">
        <Card flush>
          <CardHead title="Accounts by role" sub="An account can hold several" />
          <div className="table-wrap">
            <table className="data">
              <tbody>
                {data.byRole.map((r) => (
                  <tr key={r.role}>
                    <td>
                      <Link to={`/users?role=${r.role}`}>{humanise(r.role)}</Link>
                    </td>
                    <td className="cell-strong" style={{ textAlign: 'right' }}>
                      {num(r.count)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        <Card flush>
          <CardHead title="Users by location" sub="Where a place is known for the account" />
          {data.topLocations.length === 0 ? (
            <Empty icon="⌘" title="No locations recorded yet" body="A user's place is saved when they pick one in the app." />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <tbody>
                  {data.topLocations.map((l) => (
                    <tr key={l.label}>
                      <td>
                        <Link to={`/users?location=${encodeURIComponent(l.label)}`}>{l.label}</Link>
                      </td>
                      <td className="cell-strong" style={{ textAlign: 'right' }}>
                        {num(l.count)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
